var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { memo, useMemo, useState } from 'react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LinkIcon from '@mui/icons-material/Link';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { RewardType } from 'root/src/shared/rewards/constants';
import { KeyProductMnemonicIdResolver } from 'root/src/shared/rewards/services/productResolver.service';
import getBountyName from 'root/src/shared/util/getBountyName';
import { addHttpsProtocol } from 'root/src/shared/util/protocolUtil';
import { buildAssetUrl } from 'root/src/client/v2/common/helpers/assetUrl';
import Link from 'root/src/client/web/base/Link';
import { SecretCopyField } from 'root/src/client/v2/common/components/ui/secret-copy-field';
import { useUserData } from 'root/src/client/v2/userData/hooks/useUserData';
import { resolveLinkMarketingCopy } from 'root/src/shared/util/resolveLinkMarketingCopy';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from 'root/src/client/v2/common/components/ui/tooltip';
import { useRedesignModal } from 'root/src/client/v2/common/contexts/redesignModal.context';
import { RedesignModalShell } from 'root/src/client/v2/common/components/RedesignModalShell';
import { Checkbox } from 'root/src/client/v2/common/components/ui/checkbox';
import { Button } from 'root/src/client/v2/common/components/ui/button';
import { motion, useAnimationControls } from 'framer-motion';
import { toast } from 'root/src/client/v2/common/components/ui/sonner';
const SectionLabel = ({ children }) => (<p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{children}</p>);
const Heading = memo(() => (<div className="flex flex-col items-center gap-2 text-center">
		<span className="inline-flex items-center gap-1.5 rounded-full border border-solid border-neon-green/30 bg-neon-green/10 px-2.5 py-1 text-[11px] font-semibold text-neon-green">
			<CheckCircleIcon sx={{ fontSize: 14 }}/>
			Dare accepted
		</span>
		<h2 className="font-display text-xl font-bold text-foreground">Ready player one?</h2>
		<p className="text-xs text-muted-foreground">Your reward is below. Copy it, head to the store, and complete the dare.</p>
	</div>));
const ChatAutoShareHeader = memo(() => (<div className="flex items-center gap-2">
		<span className="text-sm font-bold uppercase tracking-wide text-foreground">Chat Auto-Share</span>
		<span className="rounded-full bg-neon-cyan/15 px-1.5 py-0.5 text-[10px] font-bold uppercase leading-none text-neon-cyan">New</span>
		<TooltipProvider delayDuration={200}>
			<Tooltip>
				<TooltipTrigger asChild>
					<button type="button" aria-label="About chat auto-share" className="flex h-5 w-5 items-center justify-center rounded-full text-muted-foreground transition-colors hover:text-foreground">
						<InfoOutlinedIcon sx={{ fontSize: 16 }}/>
					</button>
				</TooltipTrigger>
				<TooltipContent className="max-w-[260px] text-left">
					When enabled, Dare Drop automatically posts your share link as a chat announcement while you
					are live streaming the sponsored game. Auto adjusts frequency to your viewer count to avoid spam.
				</TooltipContent>
			</Tooltip>
		</TooltipProvider>
	</div>));
const DarePill = ({ children }) => (<span className="rounded-full border border-solid border-white/[0.08] bg-white/[0.04] px-2.5 py-0.5 text-[11px] text-muted-foreground">{children}</span>);
const DareView = memo(({ dare }) => {
    const game = dare.games[0];
    const region = useMemo(() => (dare.product.type === RewardType.KEY
        ? new KeyProductMnemonicIdResolver(dare.product.id).getReadableRegion()
        : 'Worldwide'), [dare]);
    const bounty = getBountyName({ name: dare.product.name, type: dare.product.type, amount: dare.amount }, false);
    return (<div className="flex w-full items-stretch gap-4 rounded-xl border border-solid border-white/[0.06] bg-white/[0.02] p-4">
			{(game === null || game === void 0 ? void 0 : game.image) && <img src={buildAssetUrl(game.image, { w: 320, q: 80, fmt: 'webp' })} alt={game.label} className="h-32 w-24 flex-shrink-0 rounded-lg object-cover"/>}
			<div className="flex min-w-0 flex-1 flex-col justify-center gap-2.5">
				<span className="text-sm font-semibold leading-snug text-foreground">{dare.title}</span>
				<div className="flex flex-wrap gap-1.5">
					<DarePill>Level {dare.level}</DarePill>
					<DarePill>{region}</DarePill>
				</div>
				<div>
					<span className="text-[10px] uppercase tracking-wider text-muted-foreground">Loot for your fans</span>
					<p className="text-sm text-foreground">{bounty}</p>
				</div>
			</div>
		</div>);
});
const DropImage = memo(({ dropEvent }) => {
    var _a;
    const game = (_a = dropEvent.dares[0]) === null || _a === void 0 ? void 0 : _a.games[0];
    if (!game) {
        return null;
    }
    return (<div className="flex w-full justify-center rounded-xl border border-solid border-white/[0.06] bg-white/[0.02] p-4">
			<img src={buildAssetUrl(game.image, { w: 320, q: 80, fmt: 'webp' })} alt={game.label} className="h-40 w-28 rounded-lg object-cover"/>
		</div>);
});
const Products = memo(({ products: allProducts, shortUrl, isGameFree }) => {
    // Only redeemable rewards belong here. A bundle hands the creator the keys inside it, so the
    // bundle record itself would just read as an unusable id.
    const products = useMemo(() => allProducts.filter(product => !product.isBundle), [allProducts]);
    if (!products.length) {
        return (<div className="flex w-full flex-col items-center gap-2 rounded-xl border border-solid border-white/[0.06] bg-white/[0.02] p-4 text-center text-sm text-muted-foreground">
				<span>No initial key for this drop</span>
				<Link href={shortUrl}>{isGameFree ? 'Free to play' : 'Get the game here to dare'}</Link>
			</div>);
    }
    return (<ul data-wg-notranslate className="flex w-full flex-col gap-3">
			{/* A bundle can hand over several copies of the same key, so the name alone is not unique. */}
			{products.map(product => (<li key={`${product.name}|${product.rewardData}`}>
					<SecretCopyField label={product.name} value={product.rewardData}/>
				</li>))}
		</ul>);
});
const ShareLinkCard = memo(({ url }) => {
    const [copied, setCopied] = useState(false);
    const copy = () => __awaiter(void 0, void 0, void 0, function* () {
        try {
            yield navigator.clipboard.writeText(url);
            setCopied(true);
            toast.success('Link copied');
            setTimeout(() => setCopied(false), 1500);
        }
        catch (error) {
            toast.error('Could not copy to clipboard');
        }
    });
    return (<div className="w-full">
			<div className="mb-2 flex items-center gap-1.5">
				<SectionLabel>Share link</SectionLabel>
				<TooltipProvider delayDuration={200}>
					<Tooltip>
						<TooltipTrigger asChild>
							<button type="button" aria-label="About the share link" className="-mt-2 flex h-4 w-4 items-center justify-center rounded-full text-muted-foreground transition-colors hover:text-foreground">
								<InfoOutlinedIcon sx={{ fontSize: 14 }}/>
							</button>
						</TooltipTrigger>
						<TooltipContent className="max-w-[240px] text-left">
							Sharing this link in your stream helps the developers credit your participation in the drop.
						</TooltipContent>
					</Tooltip>
				</TooltipProvider>
			</div>
			<TooltipProvider delayDuration={300}>
				<Tooltip>
					<TooltipTrigger asChild>
						<button type="button" onClick={copy} data-wg-notranslate aria-label="Copy share link" className="flex w-full items-center gap-3 rounded-xl border border-solid border-neon-cyan/20 bg-neon-cyan/[0.05] px-3 py-2.5 text-left transition-colors hover:border-neon-cyan/40 hover:bg-neon-cyan/[0.08]">
							<LinkIcon sx={{ fontSize: 18 }} className="flex-shrink-0 text-neon-cyan"/>
							<span className="min-w-0 flex-1 truncate text-sm font-medium text-neon-cyan">{url}</span>
							{copied
            ? <CheckIcon sx={{ fontSize: 18 }} className="flex-shrink-0 text-neon-green"/>
            : <ContentCopyIcon sx={{ fontSize: 16 }} className="flex-shrink-0 text-muted-foreground"/>}
						</button>
					</TooltipTrigger>
					<TooltipContent>{copied ? 'Copied!' : 'Click to copy'}</TooltipContent>
				</Tooltip>
			</TooltipProvider>
		</div>);
});
const ChatMessagePreview = ({ linkMarketingCopy, url }) => {
    var _a, _b;
    const { userData } = useUserData();
    const displayName = (_b = (_a = userData === null || userData === void 0 ? void 0 : userData.creatorData) === null || _a === void 0 ? void 0 : _a.displayName) !== null && _b !== void 0 ? _b : 'You';
    const message = resolveLinkMarketingCopy(linkMarketingCopy, url);
    const parts = message.split(/(https?:\/\/\S+)/g);
    return (<div className="w-full rounded-lg border border-solid border-white/[0.06] bg-black/20 p-3">
			<p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Chat preview</p>
			<div className="overflow-hidden rounded border border-l-[3px] border-solid border-[#a970ff]/40 border-l-[#a970ff] bg-[#a970ff]/[0.06]">
				<div className="flex items-center gap-1.5 px-2 py-1.5">
					<svg width="14" height="14" viewBox="0 0 24 24" fill="#a970ff" aria-hidden>
						<path fillRule="evenodd" clipRule="evenodd" d="m13 17 9 5V2l-9 5H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h3v5h2v-5h4Zm7-11.601-6 3.333v6.536l6 3.333V5.399ZM4 9h8v6H4V9Z"/>
					</svg>
					<span className="text-xs font-semibold text-[#a970ff]">Announcement</span>
				</div>
				<div className="flex flex-wrap items-start gap-1.5 px-2 pb-2">
					<span className="text-sm font-bold text-neon-cyan">{displayName}</span>
					<span className="break-all text-sm text-foreground/90">
						{parts.map(part => (/^https?:\/\//.test(part)
            ? <a key={part} href={part} target="_blank" rel="noopener noreferrer" className="text-[#a970ff] underline">{part}</a>
            : <span key={part}>{part}</span>))}
					</span>
				</div>
			</div>
		</div>);
};
export const RedesignInitialProductDisplayModal = memo((props) => {
    const { dropEvent, products = [], dare } = props;
    const modal = useRedesignModal();
    const [isAgreed, setIsAgreed] = useState(false);
    const [isChatSharingEnabled, setIsChatSharingEnabled] = useState(false);
    const [agreeError, setAgreeError] = useState(false);
    const shortUrl = addHttpsProtocol(props.shortUrl);
    const agreeShake = useAnimationControls();
    const onContinue = () => {
        if (!isAgreed) {
            // Highlight the checkbox + shake instead of an inline error (no layout shift).
            setAgreeError(true);
            void agreeShake.start({ x: [0, -5, 5, -5, 5, 0], transition: { duration: 0.35 } });
            toast.error('Please tick the box to agree before continuing');
            return;
        }
        if (isChatSharingEnabled && props.onEnableChatSharing) {
            props.onEnableChatSharing();
        }
        else {
            modal.hide();
        }
    };
    return (<RedesignModalShell className="sm:max-w-lg">
			<Heading />

			{dare ? <DareView dare={dare}/> : <DropImage dropEvent={dropEvent}/>}

			<div className="w-full">
				<SectionLabel>Your reward</SectionLabel>
				<Products products={products} shortUrl={shortUrl} isGameFree={dropEvent.isGameFree}/>
			</div>

			<ShareLinkCard url={shortUrl}/>

			<div className="flex w-full flex-col gap-3 rounded-xl border border-solid border-white/[0.06] bg-white/[0.02] p-4">
				<ChatAutoShareHeader />
				<label htmlFor="ipd-chat" className="flex cursor-pointer select-none items-center gap-2 text-sm text-foreground">
					<Checkbox id="ipd-chat" checked={isChatSharingEnabled} onCheckedChange={setIsChatSharingEnabled}/>
					Auto-share link in chat
				</label>
				<ChatMessagePreview linkMarketingCopy={props.linkMarketingCopy} url={shortUrl}/>
			</div>

			<div className="h-px w-full bg-white/[0.06]"/>

			<motion.label htmlFor="ipd-agree" animate={agreeShake} className="flex w-full cursor-pointer select-none items-center gap-2 rounded-lg px-2 py-1 text-sm text-foreground">
				<Checkbox id="ipd-agree" checked={isAgreed} onCheckedChange={(value) => { setIsAgreed(value); setAgreeError(false); }} className={agreeError ? 'border-destructive ring-2 ring-destructive/40' : undefined}/>
				<span>I agree to post this link with my content <span className="font-bold text-destructive">*</span></span>
			</motion.label>

			<Button variant="brand" onClick={onContinue} className="w-full rounded-lg text-sm font-semibold">Continue</Button>
		</RedesignModalShell>);
});
